import { useState, useMemo, Suspense } from 'react';
import MarketAlertCard from '@/components/dashboard-strykr/MarketAlertCard';
import CalendarAlertCard from '@/components/dashboard-strykr/CalendarAlertCard';
import { useAlerts } from '@/hooks/use-alerts';
import { MobileNav } from '@/components/MobileNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { format, formatDistanceToNow, parseISO, isBefore, isToday, addDays, isWithinInterval } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CalendarAlert, MarketAlert, NewsAlert } from '@/utils/api';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Bell,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertCircle,
  BarChart,
  RefreshCcw,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  LineChart,
  Globe,
  Layers,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

function AlertsContent() {
  const { 
    calendarAlerts, 
    marketAlerts,
    newsAlerts, 
    isLoadingCalendar, 
    isLoadingMarket,
    isLoadingNews, 
    errorCalendar, 
    errorMarket,
    errorNews 
  } = useAlerts();
  const isMobile = useIsMobile();
  
  const [expandedCalendarId, setExpandedCalendarId] = useState<number | null>(null);
  const [expandedMarketId, setExpandedMarketId] = useState<number | null>(null);
  const [expandedNewsId, setExpandedNewsId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [articlePage, setArticlePage] = useState<number>(0);

  const toggleCalendarAlert = (alertId: number) => {
    setExpandedCalendarId(expandedCalendarId === alertId ? null : alertId);
  };

  const toggleMarketAlert = (alertId: number) => {
    setExpandedMarketId(expandedMarketId === alertId ? null : alertId);
  };
  
  const toggleNewsAlert = (alertId: number) => {
    setExpandedNewsId(expandedNewsId === alertId ? null : alertId);
  };

  const getVolatilityColor = (intensity: string) => {
    switch (intensity.toLowerCase()) {
      case 'critical':
        return 'bg-red-600 text-white';
      case 'high':
        return 'bg-calendar-high text-white';
      case 'medium':
        return 'bg-calendar-medium text-white';
      case 'low':
        return 'bg-calendar-low text-black';
      default:
        return 'bg-secondary text-muted-foreground';
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'HIGH':
        return 'bg-calendar-high text-white';
      case 'MEDIUM':
        return 'bg-calendar-medium text-white';
      case 'LOW':
        return 'bg-calendar-low text-black';
      default:
        return 'bg-secondary text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatTimeDistance = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Unknown';
    }
  };

  const renderCalendarCard = (alert: CalendarAlert) => {
    const isExpanded = expandedCalendarId === alert.id;
    return (
      <div key={`calendar-container-${alert.id}`} className="mb-6">
        <CalendarAlertCard 
          key={`calendar-${alert.id}`}
          alert={alert}
          isExpanded={isExpanded}
          toggleExpand={toggleCalendarAlert}
        />
      </div>
    );
  };

  // Helper function to get color class based on intensity
  const getIntensityColorClass = (intensity: string) => {
    if (!intensity) return 'bg-gray-400';
    
    switch (intensity.toLowerCase()) {
      case 'critical':
        return 'bg-red-600';
      case 'high':
        return 'bg-calendar-high';
      case 'medium':
        return 'bg-calendar-medium';
      case 'low':
        return 'bg-calendar-low';
      default:
        return 'bg-gray-400';
    }
  };

  // Helper function to get risk level color class
  const getRiskLevelColorClass = (level: string) => {
    switch (level.toUpperCase()) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-amber-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const renderMarketCard = (alert: MarketAlert) => {
    const isExpanded = expandedMarketId === alert.id;
    return (
      <MarketAlertCard 
        key={`market-${alert.id}`}
        alert={alert}
        isExpanded={isExpanded}
        toggleExpand={toggleMarketAlert}
      />
    );
  };

  const renderLoadingState = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="p-4">
          <div className="flex items-start space-x-3">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-1/4 mt-2" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderCalendarContent = () => {
    if (isLoadingCalendar) {
      return renderLoadingState();
    }
    
    if (errorCalendar) {
      return (
        <div className="p-6 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Error Loading Calendar Alerts</h3>
          <p className="text-muted-foreground">{errorCalendar}</p>
        </div>
      );
    }
    
    if (calendarAlerts.length === 0) {
      return (
        <div className="p-6 text-center">
          <CalendarIcon className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Calendar Alerts</h3>
          <p className="text-muted-foreground">You don't have any calendar alerts at the moment.</p>
        </div>
      );
    }
    
    return (
      <div>{calendarAlerts.map(renderCalendarCard)}</div>
    );
  };

  const renderMarketContent = () => {
    if (isLoadingMarket) {
      return renderLoadingState();
    }
    
    if (errorMarket) {
      return (
        <div className="p-6 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Error Loading Market Alerts</h3>
          <p className="text-muted-foreground">{errorMarket}</p>
        </div>
      );
    }
    
    if (marketAlerts.length === 0) {
      return (
        <div className="p-6 text-center">
          <BarChart className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Market Alerts</h3>
          <p className="text-muted-foreground">You don't have any market alerts at the moment.</p>
        </div>
      );
    }
    
    return (
      <div>{marketAlerts.map(renderMarketCard)}</div>
    );
  };

  // Function to extract domain from URL
  const extractDomain = (url: string) => {
    try {
      // Handle cases where url is already just a domain
      if (!url.includes('http')) {
        return url;
      }
      const domain = new URL(url).hostname;
      return domain;
    } catch {
      return url;
    }
  };

  // Define the number of articles to show per page
  const articlesPerPage = 4;

  const renderNewsCard = (alert: NewsAlert) => {
    const isExpanded = expandedNewsId === alert.id;
    
    // Get sentiment emoji
    const getSentimentEmoji = (sentiment: string) => {
      switch(sentiment) {
        case 'Bullish': return '🟢';
        case 'Bearish': return '🔴';
        default: return '⚪';
      }
    };
    
    // Get sentiment color class
    const getSentimentColorClass = (sentiment: string) => {
      switch(sentiment) {
        case 'Bullish': return 'bg-green-900/40 text-green-400 border-green-900/50';
        case 'Bearish': return 'bg-red-900/40 text-red-400 border-red-900/50';
        default: return 'bg-gray-500/40 text-gray-400 border-gray-500/50';
      }
    };
    
    return (
      <div key={`news-${alert.id}`} className="mb-6">
        <div 
          className={cn(
            "bg-black/30 border rounded-lg p-4 transition-all",
            isExpanded ? "border-purple-500/30" : "border-white/10 hover:border-white/20 cursor-pointer"
          )}
          onClick={() => !isExpanded && toggleNewsAlert(alert.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="p-2 rounded-full bg-purple-500/10 text-purple-400">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white">Market News Summary</h3>
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                    NEWS
                  </Badge>
                </div>
                <div className="mt-1 text-white/90 text-left">
                  {alert.summary}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getSentimentColorClass(alert.sentiment)}>
                {getSentimentEmoji(alert.sentiment)} {alert.sentiment}
              </Badge>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="mb-4">
                <div className="text-sm text-white/90 font-semibold mb-1">Sentiment Analysis</div>
                <div className="text-white/80 text-sm">
                  {alert.sentiment_reasoning}
                </div>
              </div>
              
              {alert.news_articles && alert.news_articles.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-white/90 font-semibold mb-2">Source Articles</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {alert.news_articles.map((article, index) => {
                      // Use the full article URL if available, otherwise use the source domain
                      const url = article.url ? article.url : 
                        (article.source.toLowerCase().includes('http') 
                          ? article.source
                          : `https://${article.source}`);
                      
                      return (
                        <div key={index} className="bg-black/20 p-3 rounded border border-white/10 flex flex-col">
                          <div className="font-medium text-sm text-white/90 line-clamp-2">{article.headline}</div>
                          <div className="text-xs text-white/70 mt-1 flex-grow">
                            <div className="line-clamp-2">{article.snippet}</div>
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-1 border-t border-white/5">
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:text-primary/80 underline transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {extractDomain(article.source)}
                            </a>
                            <div className="text-xs text-white/60">{article.date}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="mt-3 text-right">
                <div className="text-xs text-white/60">
                  <Clock className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                </div>
              </div>
            </div>
          )}
          
          {!isExpanded && (
            <div className="flex items-center mt-3 text-sm text-white/60">
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNewsContent = () => {
    if (isLoadingNews) {
      return renderLoadingState();
    }
    
    if (errorNews) {
      return (
        <div className="p-6 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Error Loading News Alerts</h3>
          <p className="text-muted-foreground">{errorNews}</p>
        </div>
      );
    }
    
    if (newsAlerts.length === 0) {
      return (
        <div className="p-6 text-center">
          <Globe className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No News Alerts</h3>
          <p className="text-muted-foreground">You don't have any news alerts at the moment.</p>
        </div>
      );
    }
    
    return (
      <div>{newsAlerts.map(renderNewsCard)}</div>
    );
  };

  const renderAllContent = () => {
    // Combine all types of alerts and sort by timestamp (newest first)
    const allAlerts = [
      ...calendarAlerts.map(alert => ({ ...alert, type: 'calendar' })),
      ...marketAlerts.map(alert => ({ ...alert, type: 'market' })),
      ...newsAlerts.map(alert => ({ ...alert, type: 'news' }))
    ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (isLoadingCalendar || isLoadingMarket) {
      return renderLoadingState();
    }

    if (errorCalendar && errorMarket) {
      return (
        <div className="p-6 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Error Loading Alerts</h3>
          <p className="text-muted-foreground">Failed to load alerts. Please try again later.</p>
        </div>
      );
    }

    if (allAlerts.length === 0) {
      return (
        <div className="p-6 text-center">
          <Bell className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Alerts</h3>
          <p className="text-muted-foreground">You don't have any alerts at the moment.</p>
        </div>
      );
    }

    return (
      <div>
        {allAlerts.map(alert => {
          if (alert.type === 'calendar') return renderCalendarCard(alert as CalendarAlert);
          if (alert.type === 'market') return renderMarketCard(alert as MarketAlert);
          return renderNewsCard(alert as NewsAlert);
        })}
      </div>
    );
  };

  // Calculate statistics for the dashboard
  const stats = useMemo(() => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const dayAfter = addDays(today, 2);
    
    const criticalCount = calendarAlerts.filter(a => 
      a.window_volatility_intensity.toLowerCase() === 'critical').length;
    const highCount = calendarAlerts.filter(a => 
      a.window_volatility_intensity.toLowerCase() === 'high').length;
    const mediumCount = calendarAlerts.filter(a => 
      a.window_volatility_intensity.toLowerCase() === 'medium').length;

    const highRiskCount = marketAlerts.filter(a => 
      a.risk_level.toUpperCase() === 'HIGH').length;
    
    const upcomingEvents = calendarAlerts.filter(a => {
      try {
        const windowStart = new Date(a.volatile_window_start);
        return isWithinInterval(windowStart, {
          start: today,
          end: dayAfter
        });
      } catch {
        return false;
      }
    }).length;

    // Get the highest strykr score alert
    const highestScoreAlert = [...calendarAlerts].sort((a, b) => 
      b.strykr_score - a.strykr_score)[0];
    
    return {
      totalAlerts: calendarAlerts.length + marketAlerts.length,
      calendarCount: calendarAlerts.length,
      marketCount: marketAlerts.length,
      criticalCount,
      highCount,
      mediumCount,
      highRiskCount,
      upcomingEvents,
      highestScoreAlert
    };
  }, [calendarAlerts, marketAlerts]);

  return (
    <div className={cn(
      "container mx-auto p-4 max-w-4xl",
      isMobile && "pb-24" // Add padding for mobile nav
    )}>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Alerts
          </h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full mb-6">
            <TabsTrigger value="all">All Alerts</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="market">Market</TabsTrigger>
            <TabsTrigger value="news">News</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-6 space-y-6">
            {renderAllContent()}
          </TabsContent>
          
          <TabsContent value="calendar" className="mt-6 space-y-6">
            {renderCalendarContent()}
          </TabsContent>
          
          <TabsContent value="market" className="mt-6 space-y-6">
            {renderMarketContent()}
          </TabsContent>
          
          <TabsContent value="news" className="mt-6 space-y-6">
            {renderNewsContent()}
          </TabsContent>
        </Tabs>
      {isMobile && <MobileNav />}
    </div>
  );
}

export default function AlertsPage() {
  return (
    <ErrorBoundary>
      <Suspense 
        fallback={
          <div className="flex h-[80vh] items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading alerts...</p>
            </div>
          </div>
        }
      >
        <AlertsContent />
      </Suspense>
    </ErrorBoundary>
  );
} 